import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Shield, FileText, GraduationCap, Send, DollarSign } from 'lucide-react';

/**
 * Site-wide legal disclaimer component for Neon Alpha Investment Club.
 *
 * The platform is positioned as an EDUCATIONAL service — not a hedge fund,
 * investment advisor, broker, or securities offering. Every page renders one
 * of these variants either inline (`compact`) or as a full panel (`full`,
 * `membership`, `payout`, `signals`, `banner`).
 */

const DISCLAIMERS = {
  general: {
    icon: AlertTriangle,
    title: 'General Disclaimer',
    body:
      'This platform is for educational, informational, and entertainment purposes only. Nothing on this platform constitutes financial advice, investment advice, legal advice, or a recommendation to buy or sell securities.',
  },
  signals: {
    icon: Send,
    title: 'Signals Disclaimer',
    body:
      'Market alerts and educational insights are commentary and research only. They are not trading recommendations or guarantees of performance.',
  },
  membership: {
    icon: GraduationCap,
    title: 'Membership Disclaimer',
    body:
      'Membership fees provide access to educational content, community features, research, and digital materials only. Membership does not represent ownership, equity, securities, or investment contracts.',
  },
  payout: {
    icon: DollarSign,
    title: 'Payout Disclaimer',
    body:
      'Any creator or affiliate payouts are compensation for platform participation, referrals, educational content creation, or approved revenue-sharing activities. They are not investment dividends or passive investment returns.',
  },
};

const DisclaimerRow = ({ id }) => {
  const d = DISCLAIMERS[id];
  const Icon = d.icon;
  return (
    <div className="flex items-start gap-3" data-testid={`disclaimer-${id}`}>
      <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
        <Icon className="text-amber-400" size={16} />
      </div>
      <div>
        <p className="text-[13px] font-semibold text-white/90">{d.title}</p>
        <p className="text-[12px] text-white/60 leading-relaxed mt-0.5">{d.body}</p>
      </div>
    </div>
  );
};

const LegalDisclaimer = ({ variant = 'full' }) => {
  // Inline compact card — used at the bottom of feature pages
  if (variant === 'compact') {
    return (
      <div
        className="bg-white/[0.03] border border-white/10 rounded-xl p-4 mt-6 backdrop-blur-sm"
        data-testid="legal-disclaimer-compact"
      >
        <div className="flex items-start gap-2">
          <AlertTriangle className="text-amber-400 flex-shrink-0 mt-0.5" size={14} />
          <div className="text-[11px] text-white/60 leading-relaxed">
            <span className="font-semibold text-white/80">Educational use only. </span>
            Neon Alpha Investment Club provides educational content, research, and community access —
            <strong> not financial, investment, legal, or tax advice</strong>. Membership and any creator
            payouts are compensation for participation, never investment returns.{' '}
            <Link to="/terms" className="text-cyan-300 hover:text-cyan-200 underline-offset-2 hover:underline">
              Terms
            </Link>{' '}
            ·{' '}
            <Link to="/privacy" className="text-cyan-300 hover:text-cyan-200 underline-offset-2 hover:underline">
              Privacy
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Top-of-page banner — keep prominent on every route
  if (variant === 'banner') {
    return (
      <div className="bg-gradient-to-r from-amber-500/10 via-amber-400/10 to-amber-500/10 border-y border-amber-400/20 py-2 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-[11px] text-amber-200">
          <AlertTriangle size={12} className="flex-shrink-0" />
          <p>
            <strong>Educational use only · Not financial advice.</strong> Content, signals, and AI outputs are
            commentary and research, not trading recommendations.
          </p>
        </div>
      </div>
    );
  }

  // Single-purpose variants — show only the requested section
  if (variant in DISCLAIMERS) {
    return (
      <div
        className="bg-white/[0.03] border border-white/10 rounded-xl p-5 backdrop-blur-sm"
        data-testid={`legal-disclaimer-${variant}`}
      >
        <DisclaimerRow id={variant} />
      </div>
    );
  }

  // Full panel — site footer + landing page. Renders ALL 4 disclaimers.
  return (
    <div
      className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 mt-8 backdrop-blur-sm"
      data-testid="legal-disclaimer-full"
    >
      <div className="flex items-center gap-2 mb-5">
        <Shield className="text-cyan-300" size={18} />
        <h3 className="font-bold text-white text-base tracking-wide uppercase">Legal & Compliance</h3>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <DisclaimerRow id="general" />
        <DisclaimerRow id="signals" />
        <DisclaimerRow id="membership" />
        <DisclaimerRow id="payout" />
      </div>

      <div className="flex items-center gap-5 mt-5 pt-5 border-t border-white/10 text-xs">
        <Link
          to="/terms"
          className="flex items-center gap-1.5 text-cyan-300 hover:text-cyan-200 font-medium"
        >
          <FileText size={13} />
          Terms & Conditions
        </Link>
        <Link
          to="/privacy"
          className="flex items-center gap-1.5 text-cyan-300 hover:text-cyan-200 font-medium"
        >
          <Shield size={13} />
          Privacy Policy
        </Link>
        <span className="text-white/40 text-[11px] ml-auto">
          Neon Alpha is an educational and research platform.
        </span>
      </div>
    </div>
  );
};

export default LegalDisclaimer;
